import { useMemo } from 'react'
import { useNews } from '../lib/queries'
import { useRegionPlace } from '../lib/currentRegion'
import { cityLinesToday } from '../lib/today'
import { useBerlinNow } from '../lib/useBerlinNow'
import { currentNews, newsDateText } from '../screens/City'
import { tidyNewsText } from '../../../src/newsText'
import type { NewsRow } from '../lib/types'
import './city.css'

/** As many as the phone's Today shows. */
const CITY_LINES = 3

/** One identity for "no lines", so the memo below is not rebuilt each render. */
const NO_LINES: string[] = []

/**
 * Today's city lines on the desk (a3-27, Doug's choice): the three the
 * phone's Today leads with — `currentNews`, then `today.ts`'s
 * `cityLinesToday` (a window over for the day dropped, closures on the
 * profile's transit lines first), the same two calls Today makes — in the Year's day pane for
 * today and under desk Browse's count. Each goes to `#/city`, where the
 * whole list is; the text is the source's (English where 6.6 wrote it),
 * tidied as City draws it, never written here.
 */
export function useCityLines(today: string): NewsRow[] {
  const news = useNews().data
  // Re-read at midnight and when the tab comes back, which is when a desk
  // tab left open would otherwise keep a march whose hours are over.
  const now = useBerlinNow()
  // C2.2 (D5): the lines of the region being looked at (the U7; the Fitchburg
  // Line), not one profile column. A region whose `news` rows have not
  // arrived (Boston's come with C4.1) has none to rank, `useNews` answers an
  // empty list, and both components below draw nothing — no empty strip.
  const lines = useRegionPlace()?.transit_lines ?? NO_LINES
  return useMemo(
    () => cityLinesToday(currentNews(news ?? [], today), now, lines).slice(0, CITY_LINES),
    [news, today, now, lines],
  )
}

const headline = (n: NewsRow) => tidyNewsText(n.title_en ?? n.title)

/** The day pane's section: up to three lines, each with its lines and date. */
export function CityPaneLines({ today }: { today: string }) {
  const lines = useCityLines(today)
  if (!lines.length) return null
  return (
    <div className="dk-city-pane"><h3>In the city</h3>
      {lines.map(n => (
        <a key={n.id} className="yd-prow" href="#/city">
          <span>
            <span className="dk-city-pane__t">{headline(n)}</span>
            <span className="yd-meta">{[n.lines.join(', '), newsDateText(n, today)].filter(Boolean).join(' · ')}</span>
          </span>
        </a>
      ))}
    </div>
  )
}

/** One line under desk Browse's count: "City · A · B · C", cut at the edge. */
export function CityStrip({ today }: { today: string }) {
  const lines = useCityLines(today)
  if (!lines.length) return null
  return (
    <a className="dk-city-strip" href="#/city" title={lines.map(headline).join('\n')}>
      <b>City</b>{lines.map(n => <span key={n.id}>{headline(n)}</span>)}
    </a>
  )
}
